// src/components/Llabaro.tsx — the LLABARO device. Ported from LLABARO.jsx to TypeScript.
// Same finite state machine (grounding §3): one red push-to-talk button switches between
// LISTEN and TALK. Pure core lives in src/core; this file is the React shell.
//   - Demo (MockEngine): talk uses a typed text field (edge case E7).
//   - Live (GeminiEngine): talk captures the native mic, transcribes+translates via Gemini.
import {
  type CSSProperties,
  type Dispatch,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { MODE, initialMode, isListenSuspended, modeReducer } from "../core/mode";
import {
  type ConvAction,
  conversationReducer,
  emptyConversation,
} from "../core/conversation";
import type { ConversationState, LanguagePair, Settings, Turn } from "../core/models";
import { AI_STUDIO_URL, LIVE_INPUT_HZ, MIN_TALK_MS, TTS_VOICES } from "../config";
import { createEngine, type TranslationEngine } from "../engine";
import type { ListenHandle } from "../engine/types";
import { type AudioCapture, openCapture } from "../audio/capture";
import { enqueuePcm24k, unlock } from "../audio/playback";
import { releaseWakeLock, requestWakeLock } from "../util/wakeLock";
import {
  clearKey,
  loadKey,
  loadSettings,
  saveKey,
  saveSettings,
} from "../state/settings";
import { Ico } from "./Icons";
import LanguagePicker from "./LanguagePicker";
import { LANGS } from "../langs";

interface LlabaroProps {
  engine?: TranslationEngine; // injectable for tests
}

export default function Llabaro({ engine: injectedEngine }: LlabaroProps = {}) {
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const update = useCallback((patch: Partial<Settings>) => {
    setSettings((s) => {
      const next = { ...s, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  const [savedKey, setSavedKey] = useState<string>(() => loadKey());
  const [engine, setEngine] = useState<TranslationEngine>(
    () => injectedEngine ?? createEngine(savedKey)
  );

  const [mode, dispatchMode] = useReducer(modeReducer, initialMode(true));
  const [convo, dispatchConvo] = useReducer(conversationReducer, emptyConversation);
  const [error, setError] = useState("");
  const [micDenied, setMicDenied] = useState(false);
  const [listenError, setListenError] = useState(""); // live-listen degraded (talk still works)
  const [keyDraft, setKeyDraft] = useState("");
  const [caption, setCaption] = useState(""); // demo stand-in for captured native speech
  const [showKeyPanel, setShowKeyPanel] = useState(() => !loadKey()); // open only if no key yet
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const pair = settings.pair;
  const scrollRef = useRef<HTMLElement | null>(null);
  const captureFieldRef = useRef<HTMLInputElement | null>(null);
  const convoRef = useRef<ConversationState>(convo);
  const audioRef = useRef<AudioCapture | null>(null); // the single shared mic (listen + talk)
  const captureOpening = useRef<Promise<AudioCapture> | null>(null); // in-flight open, dedup
  const liveHandleRef = useRef<ListenHandle | null>(null); // persistent live-listen session
  const keyHeld = useRef(false);
  const modeRef = useRef(mode);
  const settingsRef = useRef(settings);
  const suspended = isListenSuspended(mode);

  // Open the single shared mic once and hand the same capture to both listen and talk.
  // Concurrent callers (the listen session's onopen + a talk press) share one in-flight open.
  const ensureCapture = useCallback(async (): Promise<AudioCapture> => {
    if (audioRef.current) return audioRef.current;
    if (!captureOpening.current) {
      captureOpening.current = openCapture()
        .then((cap) => {
          audioRef.current = cap;
          captureOpening.current = null;
          return cap;
        })
        .catch((e) => {
          captureOpening.current = null;
          throw e;
        });
    }
    return captureOpening.current;
  }, []);

  useEffect(() => {
    convoRef.current = convo;
  }, [convo]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // The dedicated live-translate model echoes the source instead of translating (its
  // target-language config is not settable via the SDK), so force the working Interpreter
  // path (native-audio model + explicit translate instruction) for all live listening.
  useEffect(() => {
    if (settings.liveMode !== "interpret") update({ liveMode: "interpret" });
  }, [settings.liveMode, update]);

  // close the mic capture on unmount
  useEffect(() => {
    return () => {
      audioRef.current?.close();
      audioRef.current = null;
    };
  }, []);

  // keep the screen awake during an active session (field use, E12)
  useEffect(() => {
    if (mode !== MODE.IDLE) void requestWakeLock();
    else releaseWakeLock();
  }, [mode]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && modeRef.current !== MODE.IDLE) {
        void requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      releaseWakeLock();
    };
  }, []);

  // --- live listen (real backend): ONE persistent Live session that survives talk turns. ---
  // Unlike the demo stream, this session is NOT rebuilt every time we return to LISTENING —
  // it stays connected across TALK_CAPTURE/TRANSLATING/SPEAKING and its audio feed is merely
  // gated (see the feed-gate effect) so we never re-translate our own talk turn or the TTS
  // playback. It tears down only when listening is disabled/degraded or the app goes IDLE.
  // A terminal listen failure does NOT drop the app to IDLE — it sets listenError, keeps the
  // app talk-capable, and surfaces a banner (a persisted BYOK key stays usable across reloads
  // even when the Live API is unavailable).
  const realListenArmed =
    engine.hasRealBackend && settings.listenEnabled && !listenError && mode !== MODE.IDLE;
  useEffect(() => {
    if (!realListenArmed) return;
    const handle = engine.startListen(
      pair,
      {
        onForeign: (src) => dispatchConvo({ type: "FOREIGN_PARTIAL", source: src, pair }),
        onTranslation: (tgt) => dispatchConvo({ type: "FOREIGN_PARTIAL", target: tgt, pair }),
        onFinal: () => dispatchConvo({ type: "FOREIGN_FINAL" }),
        onAudio: (pcm24k) => {
          // speech-to-speech: play the spoken translation when Voice output is on
          if (settingsRef.current.ttsEnabled) {
            enqueuePcm24k(pcm24k, settingsRef.current.volume);
          }
        },
        onReconnecting: (active) => setReconnecting(active),
        onError: () =>
          setListenError(
            "Live listening isn't available for this key right now — you can still hold the button to talk."
          ),
      },
      { mode: settings.liveMode, getCapture: ensureCapture }
    );
    liveHandleRef.current = handle;
    handle.setFeeding?.(modeRef.current === MODE.LISTENING);
    return () => {
      handle.stop();
      liveHandleRef.current = null;
      setReconnecting(false);
    };
  }, [realListenArmed, pair, engine, settings.liveMode, ensureCapture]);

  // Feed gate: forward mic audio to the persistent session only while actively LISTENING.
  // Pausing during talk/translate/speak gives mutual exclusion and stops the model hearing
  // its own TTS — without the cost of dropping and rebuilding the session each turn.
  useEffect(() => {
    if (!engine.hasRealBackend) return;
    liveHandleRef.current?.setFeeding?.(mode === MODE.LISTENING);
  }, [mode, engine]);

  // --- demo listen (mock engine): scripted stream, restarted each return to LISTENING. ---
  useEffect(() => {
    if (engine.hasRealBackend) return;
    if (mode !== MODE.LISTENING || listenError || !settings.listenEnabled) return;
    const handle = engine.startListen(pair, {
      onForeign: (src) => dispatchConvo({ type: "FOREIGN_PARTIAL", source: src, pair }),
      onTranslation: (tgt) => dispatchConvo({ type: "FOREIGN_PARTIAL", target: tgt, pair }),
      onFinal: () => dispatchConvo({ type: "FOREIGN_FINAL" }),
    });
    return () => handle.stop();
  }, [mode, pair, engine, listenError, settings.listenEnabled]);

  // Single shared mic lifecycle: keep it open while listening OR mid-talk; close it when
  // neither needs it (privacy: with listening off and not talking, the mic is released).
  useEffect(() => {
    if (!engine.hasRealBackend) return;
    const micNeeded =
      mode === MODE.TALK_CAPTURE ||
      (mode !== MODE.IDLE && settings.listenEnabled && !listenError);
    if (micNeeded) {
      void ensureCapture().catch(() => {
        /* open failure surfaces as micDenied on press / listenError via the session */
      });
    } else {
      audioRef.current?.close();
      audioRef.current = null;
    }
  }, [mode, engine, settings.listenEnabled, listenError, ensureCapture]);

  // autoscroll conversation
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [convo.turns]);

  // focus the capture field while holding (demo talk input only)
  useEffect(() => {
    if (mode === MODE.TALK_CAPTURE && !engine.hasRealBackend && captureFieldRef.current) {
      captureFieldRef.current.focus();
    }
  }, [mode, engine]);

  const press = useCallback(() => {
    setError("");
    dispatchConvo({ type: "FOREIGN_FINAL" }); // close any open foreign turn so talk stays separate
    dispatchMode({ type: "PRESS" });
    if (engine.hasRealBackend) {
      void (async () => {
        try {
          await unlock(); // resume AudioContext on the user gesture (iOS)
          const cap = await ensureCapture(); // the same mic the live session uses
          cap.startBuffering();
          setMicDenied(false);
        } catch {
          setMicDenied(true);
          dispatchMode({ type: "CANCEL" }); // no mic → discard, resume listen
        }
      })();
    }
  }, [engine, ensureCapture]);

  const release = useCallback(async () => {
    if (engine.hasRealBackend) {
      // --- live talk path: stop capture, transcribe + translate the clip ---
      const cap = audioRef.current;
      const clip = cap ? cap.stopBuffering() : null;
      const durMs = clip ? (clip.length / LIVE_INPUT_HZ) * 1000 : 0;
      if (!clip || durMs < MIN_TALK_MS) {
        dispatchMode({ type: "CANCEL" }); // E10: too short → clean cancel, resume listen
        return;
      }
      dispatchMode({ type: "RELEASE" });
      dispatchConvo({ type: "NATIVE_ADD", source: "", target: "…", pair, status: "translating" });
      try {
        const { sourceText, targetText } = await engine.transcribeAndTranslate(
          { pcm16k: clip.buffer as ArrayBuffer, sampleRate: 16000 },
          pair
        );
        if (!sourceText && !targetText) {
          setError("Didn't catch that — hold and try again.");
          patchLastNative(dispatchConvo, convoRef.current, { status: "error", targetText: "" });
          dispatchMode({ type: "TRANSLATE_ERROR" });
          return;
        }
        patchLastNative(dispatchConvo, convoRef.current, {
          sourceText,
          targetText,
          status: "final",
        });
        dispatchMode({ type: "TRANSLATED", ttsEnabled: settings.ttsEnabled });
        if (settings.ttsEnabled && settings.autoSpeak) {
          try {
            await engine.speak(targetText, settings.voice, pair.foreign, settings.volume);
          } catch (e) {
            console.error("[LLABARO] auto-speak failed:", e);
          }
        }
        dispatchMode({ type: "SPOKEN" });
      } catch (e) {
        console.error("[LLABARO] talk failed:", e);
        setError(
          "Translation failed — " +
            (e instanceof Error ? e.message : "check your key or connection.")
        );
        dispatchMode({ type: "TRANSLATE_ERROR" });
      }
      return;
    }

    // --- demo/text path: commit the caption field (empty -> canned) ---
    let text = caption.trim();
    if (!text) {
      const fallback = [
        "Yes, where is the station?",
        "How much is this?",
        "One ticket please",
        "Thank you very much",
      ];
      text = fallback[convo.counter % fallback.length];
    }
    setCaption("");
    dispatchMode({ type: "RELEASE" });
    dispatchConvo({
      type: "NATIVE_ADD",
      source: text,
      target: "…",
      pair,
      status: "translating",
    });
    try {
      const tgt = await engine.translateText(text, pair.native, pair.foreign);
      patchLastNative(dispatchConvo, convoRef.current, { targetText: tgt, status: "final" });
      dispatchMode({ type: "TRANSLATED", ttsEnabled: settings.ttsEnabled });
      if (settings.ttsEnabled && settings.autoSpeak) {
        try {
          await engine.speak(tgt, settings.voice, pair.foreign, settings.volume);
        } catch (e) {
          console.error("[LLABARO] auto-speak failed:", e);
        }
      }
      dispatchMode({ type: "SPOKEN" });
    } catch (e) {
      console.error("[LLABARO] talk failed:", e);
      setError(
        "Translation failed — " +
          (e instanceof Error ? e.message : "check your key or connection.")
      );
      dispatchMode({ type: "TRANSLATE_ERROR" });
    }
  }, [caption, pair, settings, engine, convo.counter]);

  const swap = useCallback(() => {
    update({ pair: { native: pair.foreign, foreign: pair.native } });
    dispatchConvo({ type: "CLEAR" });
    setListenError(""); // new pair → retry live listen
  }, [pair, update]);

  const changePair = useCallback(
    (next: LanguagePair) => {
      if (next.native === pair.native && next.foreign === pair.foreign) return;
      update({ pair: next });
      dispatchConvo({ type: "CLEAR" });
      setListenError(""); // new pair → retry live listen
    },
    [pair, update]
  );

  const applyKey = useCallback(() => {
    const k = keyDraft.trim();
    if (k) saveKey(k);
    else clearKey();
    audioRef.current?.close();
    audioRef.current = null;
    setSavedKey(k);
    setEngine(injectedEngine ?? createEngine(k));
    setKeyDraft("");
    setError("");
    setMicDenied(false);
    setListenError("");
    if (k) setShowKeyPanel(false); // key set — collapse the panel to reclaim space
    dispatchMode({ type: "KEY_READY" });
  }, [keyDraft, injectedEngine]);

  const onClearKey = useCallback(() => {
    clearKey();
    audioRef.current?.close();
    audioRef.current = null;
    setSavedKey("");
    setEngine(injectedEngine ?? createEngine(""));
    setKeyDraft("");
    setListenError("");
    dispatchMode({ type: "KEY_READY" }); // resume the demo stream (IDLE → LISTENING)
  }, [injectedEngine]);

  const speakLine = useCallback(
    async (turn: Turn) => {
      setError("");
      try {
        await engine.speak(turn.targetText, settings.voice, turn.targetLang, settings.volume);
        dispatchConvo({ type: "MARK_SPOKEN", id: turn.id });
      } catch (e) {
        console.error("[LLABARO] speak failed:", e);
        setError("Voice output failed. " + (e instanceof Error ? e.message : ""));
      }
    },
    [engine, settings.voice, settings.volume]
  );

  const modeLabel = {
    IDLE: "Add a key or use the demo",
    LISTENING: "Listening",
    TALK_CAPTURE: "Speaking — release to translate",
    TRANSLATING: "Translating…",
    SPEAKING: "Speaking translation…",
  }[mode];

  // Listen can be degraded (listenError) or paused by the user (listenEnabled=false).
  // In both cases the app stays usable for talk — reflect that honestly.
  const listenDown = engine.hasRealBackend && !!listenError;
  const listenPaused = mode === MODE.LISTENING && !settings.listenEnabled;
  const isActivelyListening = mode === MODE.LISTENING && !listenDown && !listenPaused;
  const statusText = listenDown
    ? "Ready — hold to talk"
    : listenPaused
      ? "Listening off"
      : reconnecting && mode === MODE.LISTENING
        ? "Reconnecting…"
        : modeLabel;
  const showLivePulse = isActivelyListening;
  // Amber edge glow only when actually listening; no glow when paused/degraded/idle.
  const visualState = suspended
    ? mode.toLowerCase()
    : isActivelyListening
      ? "listening"
      : "paused";

  const deviceStyle = { "--tscale": settings.textScale } as unknown as CSSProperties;

  return (
    <div className={"llabaro state-" + visualState} style={deviceStyle}>
      {/* ---- status rail ---- */}
      <header className="rail">
        <div className="brand">
          <span className="mark">◧</span>
          <span className="wordmark">LLABARO</span>
        </div>

        <div className="pair" title="Choose languages">
          <button
            className="chipbtn"
            onClick={() => setShowLangPicker(true)}
            aria-haspopup="dialog"
            title="Choose languages"
          >
            <span className="chip chip-native">{LANGS[pair.native] || pair.native}</span>
          </button>
          <button className="swapbtn" onClick={swap} aria-label="Swap languages">
            <Ico.swap />
          </button>
          <button
            className="chipbtn"
            onClick={() => setShowLangPicker(true)}
            aria-haspopup="dialog"
            title="Choose languages"
          >
            <span className="chip chip-foreign">{LANGS[pair.foreign] || pair.foreign}</span>
          </button>
        </div>

        <div className="rail-meta">
          <span className="tag">{engine.hasRealBackend ? "BYOK" : "DEMO"}</span>
          <span className="tag">
            {pair.native.toUpperCase()}▸{pair.foreign.toUpperCase()}
          </span>
        </div>

        <button
          className={"gearbtn " + (showKeyPanel ? "active" : "")}
          onClick={() => setShowKeyPanel((v) => !v)}
          aria-label="API key settings"
          aria-expanded={showKeyPanel}
          title="API key"
        >
          <Ico.gear />
        </button>
      </header>

      {/* ---- conversation window (the hero reading surface) ---- */}
      <main className="convo" ref={scrollRef}>
        {convo.turns.length === 0 && (
          <div className="empty">
            <p className="empty-lead">Two people, one table.</p>
            <p className="empty-sub">
              Listening translates the other speaker live. Hold the red button to speak your
              language back.
            </p>
          </div>
        )}
        {convo.turns.map((t) => (
          <article key={t.id} className={"turn turn-" + t.side + " st-" + t.status}>
            <div className="turn-head">
              <span className="turn-who">
                {t.side === "foreign"
                  ? LANGS[t.sourceLang] || t.sourceLang
                  : "You · " + (LANGS[t.sourceLang] || t.sourceLang)}
              </span>
              {t.side === "native" && t.status === "final" && (
                <button
                  className="linespeak"
                  onClick={() => speakLine(t)}
                  title="Speak this"
                  aria-label="Speak this line"
                >
                  <Ico.speaker />
                  {t.spoken ? "spoken" : "speak this"}
                </button>
              )}
            </div>
            <p className="turn-target">
              {t.status === "translating" ? (
                <span className="dots">translating</span>
              ) : (
                t.targetText
              )}
            </p>
            {t.sourceText && <p className="turn-source">{t.sourceText}</p>}
          </article>
        ))}
      </main>

      {micDenied && (
        <div className="banner" role="status">
          Microphone access is needed to listen and talk. Enable it in your browser settings.
        </div>
      )}
      {listenError && (
        <div className="banner" role="status">
          {listenError}
        </div>
      )}
      {error && (
        <div className="err" role="status">
          {error}
        </div>
      )}

      {/* capture strip appears while holding */}
      <div className={"capture " + (mode === MODE.TALK_CAPTURE ? "on" : "")}>
        {engine.hasRealBackend ? (
          <div className="capture-live">
            <Ico.mic /> Listening to you… release to translate
          </div>
        ) : (
          <input
            ref={captureFieldRef}
            value={caption}
            placeholder="…speak now (demo: type here)"
            onChange={(e) => setCaption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") release();
            }}
          />
        )}
      </div>

      {/* ---- the button + controls ---- */}
      <footer className="dock">
        <div className="ptt-row">
          <button
            className={"sidetoggle listen " + (settings.listenEnabled ? "on" : "")}
            onClick={() => update({ listenEnabled: !settings.listenEnabled })}
            aria-pressed={settings.listenEnabled}
            aria-label={
              settings.listenEnabled
                ? "Listening on — tap to pause"
                : "Listening off — tap to start"
            }
            title="Continuous listening"
          >
            <Ico.ear />
          </button>

          <button
            className={"ptt " + (suspended ? "held" : "")}
            onMouseDown={press}
            onMouseUp={release}
            onMouseLeave={() => {
              if (mode === MODE.TALK_CAPTURE) release();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              press();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              release();
            }}
            onKeyDown={(e) => {
              if ((e.key === " " || e.key === "Enter") && !keyHeld.current) {
                e.preventDefault();
                keyHeld.current = true;
                press();
              }
            }}
            onKeyUp={(e) => {
              if ((e.key === " " || e.key === "Enter") && keyHeld.current) {
                e.preventDefault();
                keyHeld.current = false;
                release();
              }
            }}
            disabled={mode === MODE.TRANSLATING || mode === MODE.SPEAKING}
            aria-pressed={suspended}
            aria-keyshortcuts="Space"
            aria-label={mode === MODE.TALK_CAPTURE ? "Release to translate" : "Hold to speak"}
          >
            <span className="ptt-core">
              <Ico.mic />
            </span>
            <span className="ptt-label">{mode === MODE.TALK_CAPTURE ? "RELEASE" : "HOLD"}</span>
          </button>

          <button
            className={"sidetoggle speech " + (settings.ttsEnabled ? "on" : "")}
            onClick={() => update({ ttsEnabled: !settings.ttsEnabled })}
            aria-pressed={settings.ttsEnabled}
            aria-label={settings.ttsEnabled ? "Voice output on" : "Voice output off"}
            title="Speak translations aloud"
          >
            <Ico.speaker />
          </button>
        </div>

        <div className="statusline" role="status" aria-live="polite">
          <span className={"pulse " + (showLivePulse ? "live" : suspended ? "talk" : "")} />
          {statusText}
        </div>

        {showKeyPanel && (
          <div className="settings-panel">
            <div className="setting-row">
              <span className="setting-label">Volume</span>
              <input
                className="setting-range"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.volume}
                disabled={!settings.ttsEnabled}
                onChange={(e) => update({ volume: parseFloat(e.target.value) })}
              />
            </div>
            <div className="setting-row">
              <span className="setting-label">Text size</span>
              <input
                className="setting-range"
                type="range"
                min="1"
                max="2.2"
                step="0.1"
                value={settings.textScale}
                onChange={(e) => update({ textScale: parseFloat(e.target.value) })}
              />
            </div>
            <div className="setting-row">
              <span className="setting-label">Voice</span>
              <select
                className="setting-select"
                value={settings.voice}
                onChange={(e) => update({ voice: e.target.value })}
              >
                {TTS_VOICES.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="setting-row">
              <span className="setting-label">Auto-speak my turn</span>
              <input
                type="checkbox"
                className="setting-check"
                checked={settings.autoSpeak}
                disabled={!settings.ttsEnabled}
                onChange={(e) => update({ autoSpeak: e.target.checked })}
                aria-label="Auto-speak my translated turn on release"
              />
            </div>
            <div className="keyrow">
              <Ico.key />
            <input
              type="password"
              value={keyDraft}
              placeholder={
                savedKey
                  ? "Key saved on this device — paste a new key to replace"
                  : "Gemini API key — BYOK, stays in this browser"
              }
              onChange={(e) => setKeyDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyKey();
              }}
            />
            {keyDraft.trim() ? (
              <button onClick={applyKey}>Use key</button>
            ) : savedKey ? null : (
              <button onClick={applyKey}>Demo mode</button>
            )}
            {savedKey && (
              <button className="clearkey" onClick={onClearKey} title="Remove saved key">
                Clear
              </button>
            )}
            </div>
            <a
              className="getkey-link"
              href={AI_STUDIO_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get a free Gemini API key ↗
            </a>
          </div>
        )}
      </footer>

      {showLangPicker && (
        <LanguagePicker
          pair={pair}
          onChange={changePair}
          onSwap={swap}
          onClose={() => setShowLangPicker(false)}
        />
      )}
    </div>
  );
}

// helper: patch the most recent native turn with a partial update
function patchLastNative(
  dispatch: Dispatch<ConvAction>,
  convo: ConversationState,
  patch: Partial<Turn>
): void {
  for (let i = convo.turns.length - 1; i >= 0; i--) {
    if (convo.turns[i].side === "native") {
      dispatch({ type: "NATIVE_UPDATE", id: convo.turns[i].id, patch });
      return;
    }
  }
}
